<template>
  <div data-testid="data-table-action" :class="rootClasses">
  <!-- Filter toolbar - hidden by default, use showToolbar prop to show -->
  <div v-if="showToolbar" data-testid="data-table-action__filter-toolbar" class="data-table-action__filter-toolbar">
    <v-btn 
      data-testid="data-table-action__clear-filter-btn"
      v-if="hasActiveFilter && showClearFilter" 
      @click="clearfilter()" 
      outlined
      small
      color="primary"
      class="data-table-action__clear-filter-btn"
    >
      <v-icon data-testid="data-table-action__clear-filter-icon" left small>mdi-filter-remove</v-icon>
      <span>Clear Filter</span>
    </v-btn>
  </div>

  <v-card data-testid="data-table-action__card" class="shadow-off data-table-action__card">
    <div>
      <v-skeleton-loader
        data-testid="data-table-action__skeleton-loader"
        v-if="loading"
        type="table-heading, list-item-two-line, article, article, article"
        class="my-4"
        :height="autoHeight ? undefined : tableHeight"
      />
    <v-simple-table data-testid="data-table-action__table" class="data-table-action__table simple-table border-on" :height="autoHeight ? undefined : computedTableHeight" v-else>
      <template v-slot:default>
        <thead data-testid="data-table-action__thead" :class="['tableHeaderBg', theadClass]">
          <tr data-testid="data-table-action__header-row" :class="['py-2', 'tableHeaderBg', headerRowClass]">
            <th data-testid="data-table-action__selectall-header" class="pl-3 tableHeaderBg th-width-48" v-if="bulkActions" >
              <v-checkbox 
                data-testid="data-table-action__selectall-checkbox"
                dense 
                color="primary"
                :indeterminate="isDataSelected" 
                :ripple="false"
                class="ma-0 py-1 single-line" 
                hide-details
                v-model="selectAll" 
                @change="onSelectAllChange"
              ></v-checkbox>
            </th>
            
            <template v-for="(item, index) in computedHeaders">
              <th 
                data-testid="data-table-action__header-cell"
                :class="['tableHeaderBg', 'px-0', 'resizable-header', 'th-min-width-5vh', headerCellClass, item.headerClass]" 
                :key="item.value"
                :ref="'header' + index" 
                v-if="item.visible"
                draggable="true"
                @dragstart="startDrag(index)" 
                @dragend="onDragEnd"
                @dragover.prevent 
                @drop="onDrop(index)"
              >
                <div class="all-width all-height d-flex align-center">
                  <div data-testid="data-table-action__header-content" class="d-flex all-width align-center pa-2 draggable-header"
                  >
                    <p data-testid="data-table-action__header-text" class="ma-0 black--text pa-0 font-weight-medium cursor-grab" :class="[item.headerClass]">
                      {{ item.text }}
                    </p>
                    
                    <v-menu 
                      data-testid="data-table-action__column-menu"
                      v-model="menuOpen[index]"
                      :close-on-content-click="false" 
                      offset-y 
                      transition="slide-y-transition" 
                      :ref="'menu'+index"
                      content-class="elevation-3"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-tooltip data-testid="data-table-action__column-actions-tooltip" bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                            <v-btn 
                              data-testid="data-table-action__column-actions-btn"
                              v-if="item.sortable || item.filterable" 
                              icon
                             
                              v-bind="{ ...attrs, ...tooltipAttrs }"
                              v-on="{ ...on, ...tooltipOn }"
                              @click="clickedHeader = item.value; filterStage = 0; $set(menuOpen, index, true)"
                              class="ml-1"
                              :color="clearIfEmptyArrays[item.value] ? 'primary' : ''"
                              @dragstart.stop
                              @dragend.stop
                            >
                              <v-icon data-testid="data-table-action__column-actions-icon-active" v-if="clearIfEmptyArrays[item.value]">mdi-filter-menu</v-icon>
                              <v-icon data-testid="data-table-action__column-actions-icon-inactive" v-else>mdi-menu-down</v-icon>
                            </v-btn>
                          </template>
                          <span>Column Actions</span>
                        </v-tooltip>
                      </template>

                      <ColumnFilter
                        data-testid="data-table-action__column-filter"
                        v-if="item.filterable"
                        :header="item"
                        :table-data="filterSourceData"
                        :preserve-options-on-filter="!hasFilterOptionsData"
                        :selected-values="selectedFilters[item.value] || []"
                        :none-sentinel="noneSentinel"
                        :show-apply-button="showFilterApplyButton"
                        :show-save-button="computedShowFilterSaveButton"
                        :is-open="menuOpen[index]"
                        auto-open
                        @apply="vals => { selectedHeader = item; handleFilterApply(vals, index); }"
                        @save="vals => { selectedHeader = item; handleFilterSave(vals, index); }"
                        @close="closeMenu(index)"
                      />
                    </v-menu>
                  </div>
                  
                  <span 
                    data-testid="data-table-action__resizer-handle"
                    v-if="item.value != 'action'" 
                    class="resizer-handle"
                    @mousedown.stop="initResize($event, index)"
                    @dragstart.stop
                    @dragend.stop
                  ></span>
                </div>
              </th>
            </template>
          </tr>
        </thead>
        
        <tbody data-testid="data-table-action__tbody" class="data-table-action__tbody">
          <!-- Empty state when no data -->
          <tr v-if="isTableEmpty" data-testid="data-table-action__empty-row" class="data-table-action__empty-row">
            <td 
              :colspan="totalColumnCount" 
              class="data-table-action__empty-cell"
              data-testid="data-table-action__empty-cell"
            >
              <div class="data-table-action__empty-content">
                <v-icon 
                  data-testid="data-table-action__empty-icon"
                  size="40" 
                  color="grey lighten-1"
                  class="mb-2"
                >
                  {{ emptyStateIcon }}
                </v-icon>
                <p 
                  data-testid="data-table-action__empty-title"
                  class="text-subtitle-1 grey--text text--darken-1 mb-1 ma-0"
                >
                  {{ emptyStateTitle }}
                </p>
                <p 
                  data-testid="data-table-action__empty-subtitle"
                  class="caption grey--text mb-0 ma-0"
                >
                  {{ emptyStateSubtitle }}
                </p>
              </div>
            </td>
          </tr>
          <!-- Normal data rows -->
          <slot v-else name="tbody" :headers="computedHeaders" />
        </tbody>
      </template>
    </v-simple-table>
     </div>
  </v-card>
</div>
</template>
<script>
import './DataTableAction.scss';
import ColumnFilter from './ColumnFilter.vue';
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEBOUNCE_FILTER_MS,
  NONE_SENTINEL,
  LOG_PREFIX
} from './constants.js';
export default {
  props: {
    headers: {
      type: Array,
      required: true,
    },
    theadClass: {
      type: [String, Array, Object],
      default: ''
    },
    headerRowClass: {
      type: [String, Array, Object],
      default: ''
    },
    headerCellClass: {
      type: [String, Array, Object],
      default: ''
    },
    tabledata: {
      type: Array,
      required: true,
    },
    originalData: {
      type: Array,
      default: null,
    },
    /**
     * Dataset for column filter checkbox/range options (pre-column-filter scope).
     * When set with rows, used instead of tabledata so applying a column filter does not
     * shrink available values. Must match the rows visible before column filtering
     * (e.g. stage-filtered list), not the fully unscoped originalData.
     * Null or an empty array falls back to tabledata.
     */
    filterOptionsData: {
      type: Array,
      default: null,
    },
    itemsperpage:{
        type:Number,
        default: DEFAULT_ITEMS_PER_PAGE
    },
    searchterm:{
        type:String,
        default:''
    },
    selectAllFunction:{
      type:Function,
      default: null
    },
    isDataSelected:{
      type:Boolean,
      default:false
    },
    bulkActions:{
      type:Boolean,
      default:true
    },
    checkalldata:{
      type:Boolean,
      default:false,
    },
    selectfilter:{
      type:Object,
      default: () => ({}),
      validator: value => typeof value === 'object' && value !== null && !Array.isArray(value),
    },
    page_details:{
      type:Object,
      default: () => ({}),
    },
    /**
     * @deprecated Use showFilterSaveButton instead. Kept for backward compatibility.
     */
    enableSaveOption: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to show the Apply button in filter dropdown (applies filter without persisting)
     */
    showFilterApplyButton: {
      type: Boolean,
      default: true
    },
    /**
     * Whether to show the Save button in filter dropdown (persists filter to backend)
     */
    showFilterSaveButton: {
      type: Boolean,
      default: false
    },
    showClearFilter: {
      type: Boolean,
      default: true
    },
    /**
     * Whether to show the built-in filter toolbar row above the table.
     * Set to false to hide and use external clear filter button in parent.
     */
    showToolbar: {
      type: Boolean,
      default: false
    },
    /**
     * When true, the table uses flex layout to automatically fill available parent space.
     * This eliminates gaps between table content and pagination.
     * When false, uses explicit tableHeight prop for height control.
     * @default true
     */
    autoHeight: {
      type: Boolean,
      default: true
    },
    /**
     * Table height - only used when autoHeight is false.
     * Can be a fixed value like '500px' or responsive like 'calc(100vh - 300px)'.
     * Ignored when autoHeight is true (flex layout controls height).
     */
    tableHeight: {
      type: String,
      default: 'calc(100vh - 320px)'
    },
    /**
     * Optional: Number of rows actually displayed in the slot.
     * Use this when tabledata contains unfiltered data but slot shows filtered rows.
     * When provided, this overrides tabledata.length for empty state detection.
     */
    displayedRowCount: {
      type: Number,
      default: null
    },
  },
  data() {
    return {
        localHeaders: [],
        selectedFilters:{},
        selectedFilterItems: [],
        filterStage:0,
        selectItem:[],
        selectAll:false,
        currentIndex: null,
        startX: 0,
        draggingIndex: null,
        startWidth: 0,
        searchfilters:'',
  filteredColumnItems:[],
  selectedHeader:null,
        lastAppliedFilterSignature: "",   // NEW: to avoid re-applying same filter
        pendingFilter: null,              // NEW: for debouncing
  // selectAllStatus removed in favor of computed visible-only helpers
        noneSentinel: NONE_SENTINEL, // Sentinel to represent "no values selected"
        applyFilterOnChange: true, // Watcher guard flag
        mountedOnce:false, // Track if mounted already ran
        menuOpen: [],  // Track open state for each v-menu
        loading:false,
        debounceFilterMs: DEBOUNCE_FILTER_MS,
        // Track whether filter was saved to backend (vs just applied locally)
        isFilterSavedToBackend: false,
    };
  },
  mounted() {
    // Initialize local headers copy
    this.localHeaders = JSON.parse(JSON.stringify(this.headers));
    // Initial load forces application
    this._initFilters({ force:true });
    this.mountedOnce = true;
  },
  activated() {
    // When returning via <keep-alive>, avoid forcing if nothing changed
    this._initFilters({ force:false });
  },
  beforeDestroy() {
    // Clean up event listeners to prevent memory leaks
    document.documentElement.removeEventListener('mousemove', this.doResize, false);
    document.documentElement.removeEventListener('mouseup', this.stopResize, false);
    // Clear any pending filter debounce timer
    if (this._filterTimer) {
      clearTimeout(this._filterTimer);
      this._filterTimer = null;
    }
    // Reset component state to prevent stale data on re-entry
    this.selectedFilters = {};
    this.selectedHeader = null;
    this.menuOpen = [];
    this.filterStage = 0;
  },
  components: { ColumnFilter },
  computed: {
    /**
     * Dynamic root element classes based on autoHeight mode
     */
    rootClasses() {
      return {
        'data-table-action': true,
        'data-table-action--auto-height': this.autoHeight,
        'data-table-action--fixed-height': !this.autoHeight
      };
    },
    computedHeaders() {
      // Use localHeaders if populated (after drag), otherwise use prop
      return this.localHeaders.length > 0 ? this.localHeaders : this.headers;
    },
    /**
     * Computed property to determine if Save button should be shown.
     * Supports both new showFilterSaveButton prop and legacy enableSaveOption prop.
     */
    computedShowFilterSaveButton() {
      // New prop takes precedence, fallback to legacy prop for backward compatibility
      return this.showFilterSaveButton || this.enableSaveOption;
    },
    /**
     * True when caller supplied a non-empty filterOptionsData override.
     * Empty arrays are treated as unset (e.g. initial load) so we fall back to tabledata.
     */
    hasFilterOptionsData() {
      return Array.isArray(this.filterOptionsData) && this.filterOptionsData.length > 0;
    },
    /**
     * Dataset for column filter option lists.
     * Prefer filterOptionsData (scoped, pre-column-filter); fall back to tabledata.
     * originalData is only for empty-state detection, not filter options.
     */
    filterSourceData() {
      return this.hasFilterOptionsData ? this.filterOptionsData : this.tabledata;
    },
    tableWrapperStyle() {
      return this.tabledata.length > 0
        ? {height: '700px', overflowY: 'auto'}
        : {height: 'auto', overflowY: 'visible'};
    },
    selectedCheckboxCount() {
      return this.filteredColumnItems.filter(item => item.status).length;
    },
    hasActiveFilter() {
      // Active if any column has non-empty array, NONE_SELECTED sentinel, or a range object with at least one bound
      return Object.values(this.selectedFilters || {}).some(v => {
        if (!v) return false;
        if (Array.isArray(v)) return v.length > 0;
        // date-range { from, to } or number-range { min, max }: active if any bound is set
        if (typeof v === 'object') return Object.values(v).some(bound => bound !== null && bound !== undefined && bound !== '');
        return false;
      });
    },
    clearIfEmptyArrays() {
      const filteredHeader = {};
      Object.entries(this.selectfilter).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (Array.isArray(value) && value.length === 0) return;
        // For range objects: only mark active if at least one bound is set
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          if (Object.values(value).every(v => v === null || v === undefined || v === '')) return;
        }
        filteredHeader[key] = value;
      });
      return filteredHeader;
    },
    /**
     * Determines if the table is empty (no data to display).
     * Uses displayedRowCount prop if provided (for cases where tabledata differs from slot content),
     * otherwise falls back to tabledata.length.
     */
    isTableEmpty() {
      // If displayedRowCount prop is explicitly provided (including 0), use it
      if (this.displayedRowCount !== null && this.displayedRowCount !== undefined) {
        return this.displayedRowCount === 0;
      }
      // Otherwise fall back to tabledata length
      return !this.tabledata || this.tabledata.length === 0;
    },
    /**
     * Checks if original data exists but is filtered out
     * (i.e., we have source data but filters reduced it to zero)
     */
    isFilteredEmpty() {
      // Check if we have source data (either from originalData prop or tabledata)
      const sourceData = this.originalData || this.tabledata;
      const hasSourceData = sourceData && sourceData.length > 0;
      // Table is filtered empty if: display is empty + we have active filters + source has data
      return this.isTableEmpty && this.hasActiveFilter && hasSourceData;
    },
    /**
     * Total column count for colspan (includes bulk action checkbox if enabled)
     */
    totalColumnCount() {
      const visibleHeaders = this.computedHeaders.filter(h => h.visible !== false);
      return this.bulkActions ? visibleHeaders.length + 1 : visibleHeaders.length;
    },
    /**
     * Icon to display in empty state
     */
    emptyStateIcon() {
      return this.isFilteredEmpty ? 'mdi-filter-off-outline' : 'mdi-table-off';
    },
    /**
     * Title message for empty state
     */
    emptyStateTitle() {
      return this.isFilteredEmpty 
        ? 'No data for applied filters' 
        : 'No data to show';
    },
    /**
     * Subtitle/helper message for empty state
     */
    emptyStateSubtitle() {
      return this.isFilteredEmpty 
        ? 'Try adjusting or clearing your filters to see more results.' 
        : 'There is no data available at this time.';
    },
    /**
     * Computed table height - behavior depends on autoHeight mode
     * - autoHeight=true: Always returns null (flex layout handles sizing)
     * - autoHeight=false: Returns 'auto' when empty, otherwise tableHeight prop
     */
    computedTableHeight() {
      // In autoHeight mode, let flex layout control height (no explicit height)
      if (this.autoHeight) {
        return this.isTableEmpty ? 'auto' : null;
      }
      // In fixed height mode, use tableHeight prop unless empty
      return this.isTableEmpty ? 'auto' : this.tableHeight;
    },
    },
  watch: { 
    headers: {
      handler(newVal) {
        // Sync localHeaders when headers prop changes
        this.localHeaders = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
      immediate: false
    },
    selectfilter: {
      handler(newVal) {
        // If we already have a persisted filter and parent sends empty, keep persisted
        try {
          const persisted = this._loadPersistedFilterFromStore();
          if (persisted && (!newVal || Object.keys(newVal).length === 0)) {
            // Don't override with empty incoming; ensure we have something applied
            if (Object.keys(this.selectedFilters || {}).length === 0) {
              // Validate persisted filter keys before applying
              const validatedPersisted = this._validateFilterKeys(persisted);
              this.selectedFilters = validatedPersisted;
              this.isFilterSavedToBackend = Object.keys(validatedPersisted).length > 0;
              this._applyFilterIfChanged(validatedPersisted, { force: true });
              this.syncStatuses();
            }
            return; // skip further processing
          }
        } catch(e) { 
          // Log the error for debugging, but continue gracefully
          console.error(`${LOG_PREFIX} Error loading persisted filter from store:`, e); 
        }

        if (!this.applyFilterOnChange) {
          this.selectedFilters = newVal;  // just sync local copy
          return;
        }
        // Validate incoming filter keys before applying
        const validatedFilter = this._validateFilterKeys(newVal || {});
        this.selectedFilters = validatedFilter;
        
        // Mark as saved to backend if incoming filter has data and save option is enabled
        // This handles the case where filter is loaded from getCustomLayout API
        if (Object.keys(validatedFilter).length > 0 && this.computedShowFilterSaveButton) {
          this.isFilterSavedToBackend = true;
        }
        
        this._scheduleFilter(validatedFilter);
        this.syncStatuses();
      },
      deep: true,
      immediate: false,   // CHANGED: was true (caused double-run with mounted)
    },
    /**
     * Watch hasActiveFilter computed and emit to parent for external clear button
     */
    hasActiveFilter: {
      handler(newVal) {
        this.$emit('filter-active-change', newVal);
      },
      immediate: true
    },
    // selectedHeader() {
    //   // Rebuild unique value list only when user changes target column
    //   this.filteredUniqueDataItems();
    // }
  },
  methods:{
    /**
     * Public method to clear filters - can be called via $refs from parent
     */
    clearFilter() {
      this.clearfilter();
    },
    // DRY: shared initialization for mounted & activated
    _initFilters({ force = false } = {}) {
      // Prefer persisted filter if present; else fall back to incoming prop
      const persisted = this._loadPersistedFilterFromStore();
      // Validate filter keys against current headers to remove stale entries
      const validatedFilter = this._validateFilterKeys(persisted || this.selectfilter || {});
      const base = validatedFilter;
      this.selectedFilters = base;
      
      // Mark filter as saved to backend if:
      // 1. We loaded from local persistence store, OR
      // 2. Filter came from selectfilter prop AND save option is enabled (means it came from getCustomLayout API)
      const hasPersistedFilter = !!(persisted && Object.keys(persisted).length > 0);
      const hasIncomingFilter = !!(this.selectfilter && Object.keys(this.selectfilter).length > 0);
      this.isFilterSavedToBackend = hasPersistedFilter || (hasIncomingFilter && this.computedShowFilterSaveButton);
      
      this._applyFilterIfChanged(base, { force });
      // Rebuild unique items if a header already chosen
      if (this.selectedHeader) {
        this.filteredUniqueDataItems();
        this.syncStatuses();
      } else {
        // Still ensure internal statuses are consistent
        this.syncStatuses();
      }
    },
    /**
     * Validates filter keys against current headers to remove stale/invalid filter entries
     * This prevents errors when filter columns have been deleted from the system
     * @param {Object} filter - The filter object to validate
     * @returns {Object} - Cleaned filter with only valid keys
     */
    _validateFilterKeys(filter) {
      if (!filter || typeof filter !== 'object') return {};
      
      // Get valid header values from both headers and computedHeaders
      const validHeaderValues = new Set();
      const headersToCheck = this.headers || [];
      headersToCheck.forEach(h => {
        if (h && h.value) validHeaderValues.add(h.value);
        if (h && h.value_en) validHeaderValues.add(h.value_en);
      });
      
      // If no headers yet (component initializing), return filter as-is
      if (validHeaderValues.size === 0) return filter;
      
      // Clean filter by removing keys that don't match any header
      const cleanedFilter = {};
      let hasInvalidKeys = false;
      
      Object.keys(filter).forEach(filterKey => {
        if (validHeaderValues.has(filterKey)) {
          cleanedFilter[filterKey] = filter[filterKey];
        } else {
          hasInvalidKeys = true;
          console.warn(`${LOG_PREFIX} Removed stale filter key: ${filterKey}`);
        }
      });
      
      // If we cleaned up invalid keys, update persisted storage as well
      if (hasInvalidKeys) {
        this.persistFilter(cleanedFilter);
      }
      
      return cleanedFilter;
    },
    filteredUniqueDataItems() {
      // retained for compatibility; actual unique building now handled in ColumnFilter
      return [];
    },
  /**
   * Updates the status of each item in filteredColumnItems based on the current filter selection.
   * Handles the NONE_SELECTED sentinel logic.
   */
    syncStatuses() {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      const selected = this.selectedFilters[col] || [];
      const noneSelectedMode = selected.includes(this.noneSentinel);
      const isFiltered = selected.length > 0 && !noneSelectedMode;
      this.filteredColumnItems.forEach(obj => {
        obj.status = noneSelectedMode ? false : (!isFiltered || selected.includes(obj.value));
      });
      // TODO - this needs to be updated when searched and set
    },
    pagecount() {
      return Math.ceil(this.tabledata?.length / this.itemsperpage);
    },
    resetStage() {
      this.filterStage = 0;
      this.selectedFilterItems = [];
    },
    onFilterByClick(header) {
      this.selectedHeader = header;
      this.searchfilters = "";
      this.filteredUniqueDataItems();
      this.syncStatuses();
      this.filterStage = 1;
    },
    handleMenuClose(isOpen) {
      if (!isOpen) {
        // this.resetStage(); 
      }
    },
    startDrag(index) {
      this.draggingIndex = index;
    },
    onDragEnd() {
      this.draggingIndex = null;
    },
    updateFixedDivHeight() {
      this.fixedDivHeight = this.$refs.fixedDiv.offsetHeight;
    },
    onDrop(index) {
      if (this.draggingIndex === null || this.draggingIndex === index) {
        this.draggingIndex = null;
        return;
      }
      const reordered = [...this.localHeaders];
      const [draggedHeader] = reordered.splice(this.draggingIndex, 1);
      reordered.splice(index, 0, draggedHeader);
      this.localHeaders = reordered;
      this.draggingIndex = null;
      this.$emit('column-reorder', [...reordered]);
    },
    getItemData(item, key) {
      return item[key];
    },
    initResize(event, index) {

      this.currentIndex = index;
      this.startX = event.clientX;
      this.startWidth = event.target.parentElement.offsetWidth;
      document.documentElement.addEventListener('mousemove', this.doResize, false);
      document.documentElement.addEventListener('mouseup', this.stopResize, false);
    },
    doResize(event) {
      const width = this.startWidth + (event.clientX - this.startX);
      const header = this.$refs['header' + this.currentIndex][0];
      if (header) {
        const styleString = `max-width: ${width}px !important; min-width: ${width}px !important;`;
        header.setAttribute('style', styleString);
      }
    },
    stopResize() {
      document.documentElement.removeEventListener('mousemove', this.doResize, false);
      document.documentElement.removeEventListener('mouseup', this.stopResize, false);
    },
    isHeaderVisible(value) {
      const header = this.headers.find(header => header.value === value && header.visible === true);
      return header ? header.visible : false;
    },
    onSelectAllChange() {
      this.$emit('select-all-change', this.selectAll);
      // this.select(this.selectAll);
    },
    _scheduleFilter(filterObj) {
      if (this.debounceFilterMs <= 0) {
        this._applyFilterIfChanged(filterObj);
        return;
      }
      this.pendingFilter = filterObj;
      clearTimeout(this._filterTimer);
      this._filterTimer = setTimeout(() => {
        this._applyFilterIfChanged(this.pendingFilter);
        this.pendingFilter = null;
      }, this.debounceFilterMs);
    },
    // Stable JSON signature with sorted keys
    _stableSignature(obj) {
      if (!obj || typeof obj !== 'object') return '';
      return JSON.stringify(this._sortObject(obj));
    },
    _sortObject(o) {
      if (Array.isArray(o)) return o.map(v => this._sortObject(v));
      if (o && typeof o === 'object') {
        return Object.keys(o).sort().reduce((acc, fieldKey) => {
          acc[fieldKey] = this._sortObject(o[fieldKey]);
          return acc;
        }, {});
      }
      return o;
    },
    _applyFilterIfChanged(newFilter, { force = false } = {}) {
      const sig = this._stableSignature(newFilter);
      if (!force && sig === this.lastAppliedFilterSignature) return;
      this.lastAppliedFilterSignature = sig;
      this.applyTableFilter(newFilter);
      if (this.selectedHeader) this.filteredUniqueDataItems();
      // Removed forceRerender(); Vue will patch DOM efficiently without key reset
    },
    // --- Persistence helpers (Vuex) ---
    _persistStorageKey() {
  const pageName = this.page_details?.pagename || 'unknown';
  const scopeIdentifier = this.page_details?.cycle_id || this.$store?.state?.client || 'global';
  return `dt_filter:${pageName}:${scopeIdentifier}`;
    },
    _loadPersistedFilterFromStore() {
      // Only load persisted filter if page_details has a valid pagename
      if (!this.page_details?.pagename) {
        return null;
      }
      const key = this._persistStorageKey();
      return this.$store.state.tableFilters[key] || null;
    },
    persistFilter(filterObj) {
      // Only persist filter if page_details has a valid pagename
      if (!this.page_details?.pagename) {
        return;
      }
      if (!filterObj || typeof filterObj !== 'object') return;
      const cleaned = Object.keys(filterObj).reduce((acc, fieldKey)=>{
        const fieldValue = filterObj[fieldKey];
        if (Array.isArray(fieldValue)) {
          if (fieldValue.length > 0) acc[fieldKey] = fieldValue.slice();
        } else if (fieldValue !== undefined && fieldValue !== null) {
          acc[fieldKey] = fieldValue;
        }
        return acc;
      }, {});
      const key = this._persistStorageKey();
      if (Object.keys(cleaned).length > 0) {
        this.$store.commit('setTableFilter', { key, filter: cleaned });
      } else {
        this.$store.commit('clearTableFilter', key);
      }
    },
    clearPersistedFilter() {
      const key = this._persistStorageKey();
      this.$store.commit('clearTableFilter', key);
    },
    applyTableFilter(item){  
      this.persistFilter(item);
      this.$emit('filter-item',item);
    },
    /**
     * Clears all active filters.
     * Uses Vuex action for backend API calls (architecture compliance).
     * Only calls the backend API if:
     * 1. showFilterSaveButton is true (persistence is enabled for this table)
     * 2. Filter was actually saved to backend (isFilterSavedToBackend flag)
     * Otherwise, just clears local state without API call.
     */
    async clearfilter(){
      this.selectedHeader = null;
      this.loading = true;
      
      try {
        // Check if we should call backend API
        const hasPageDetails = Object.keys(this.page_details || {}).length !== 0;
        const shouldCallBackendApi = hasPageDetails && this.computedShowFilterSaveButton && this.isFilterSavedToBackend;
        
        if (shouldCallBackendApi) {
          // Filter was saved to backend - dispatch Vuex action to remove
          await this.$store.dispatch('clearCustomLayout', {
            axios: this.axios,
            page: this.page_details.pagename,
            cycleId: this.page_details.cycle_id,
          });
        }
        
        // Always clear local state regardless of API call result
        this._resetFilterState();
        
        // Show success message if we didn't call API (local clear only)
        if (!shouldCallBackendApi && hasPageDetails) {
          this.$store.commit('sbSuccess', 'Filter cleared');
        }
        
      } catch (err) {
        // Handle API errors gracefully - still clear local state
        console.error(`${LOG_PREFIX} Error clearing filter:`, err);
        // Don't show error to user if it's just a "key not found" error - the filter is effectively cleared
        const errorMessage = err?.response?.data?.message || err.message || '';
        if (errorMessage.toLowerCase().includes('not found')) {
          // Key not found means filter was already cleared or never saved - this is OK
          console.warn(`${LOG_PREFIX} Filter key not found in backend - clearing local state only`);
        }
        // Note: Vuex action already commits sbError, so we don't duplicate here
        
        // Still clear local state even on error
        this._resetFilterState();
      } finally {
        this.loading = false;
      }
    },
    /**
     * Helper method to reset all filter-related state.
     * Extracted to reduce code duplication in clearfilter().
     */
    _resetFilterState() {
      this.selectedFilters = {};
      this.isFilterSavedToBackend = false;
      this.lastAppliedFilterSignature = '';
      this.clearPersistedFilter();
      this.applyTableFilter({});
      this.$emit('clear-item');
      this.filterStage = 0;
      this.menuOpen = [];
    },
    async saveFilter(filter){
      // Clone and clean filter: remove empty arrays; translate NONE_SELECTED sentinel to empty special marker
      // Also validate filter keys against current headers before saving
      const validatedFilter = this._validateFilterKeys(filter || {});
      const cleaned = {};
      Object.keys(validatedFilter).forEach(fieldKey => {
        const arr = validatedFilter[fieldKey];
        if (Array.isArray(arr)) {
          if (arr.includes(this.noneSentinel)) {
            // Represent explicit "no values" selection with sentinel for backend if required
            cleaned[fieldKey] = [this.noneSentinel];
          } else if (arr.length > 0) {
            cleaned[fieldKey] = arr.slice();
          }
        } else if (arr && typeof arr === 'object') {
          // date-range { from, to } or number-range { min, max }: include if at least one bound is set
          if (Object.values(arr).some(v => v !== null && v !== undefined && v !== '')) {
            cleaned[fieldKey] = { ...arr };
          }
        }
      });
      
      // If no valid filters remain after validation
      if (Object.keys(cleaned).length === 0) {
        // If filter was previously saved to backend, clear it (user selected all = clear filter)
        if (this.isFilterSavedToBackend) {
          await this.clearfilter();
          return;
        }
        // No previous saved filter and nothing to save - just apply empty filter locally
        this._resetFilterState();
        return;
      }
      
      try {
        await this.$store.dispatch('saveCustomLayout', {
          axios: this.axios,
            page: this.page_details.pagename,
            cycleId: this.page_details?.cycle_id,
            layout: this.page_details?.layout,
            filter: cleaned
        });
        // Mark filter as saved to backend
        this.isFilterSavedToBackend = true;
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
        console.error(`${LOG_PREFIX} Error saving filter:`, err);
      } finally {
        this.loading = false;
        this.filterStage = 0;
      }
    },
    sortBy(header,order){
      this.$emit('sort-order',header,order);
    },
     forceRerender() {
      this._applyFilterIfChanged(this.selectedFilters, { force: true });
    },
    onCheckboxChange() {
      // UPDATED: delegate to new logic (kept for compatibility if referenced)
      // no-op after refactor; retained for backward compatibility
    },
    handleFilterChange(values) {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      this.$set(this.selectedFilters, col, values);
      this.applyTableFilter(this.selectedFilters);
    },
    /**
     * Handle Apply button click from ColumnFilter.
     * Applies filter locally without persisting to backend.
     * @param {Array} values - Selected filter values
     * @param {Number} menuIndex - Index of the menu to close
     */
    handleFilterApply(values, menuIndex) {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      this.$set(this.selectedFilters, col, values);
      this.applyTableFilter(this.selectedFilters);
      // Close the menu after applying
      this.closeMenu(menuIndex);
    },
    /**
     * Handle Save button click from ColumnFilter.
     * Applies filter locally AND persists to backend.
     * @param {Array} values - Selected filter values
     * @param {Number} menuIndex - Index of the menu to close
     */
    handleFilterSave(values, menuIndex) {
      if (!this.selectedHeader || !this.selectedHeader.value) return;
      const col = this.selectedHeader.value;
      this.$set(this.selectedFilters, col, values);
      this.applyTableFilter(this.selectedFilters);
      // Save to backend
      this.saveFilter(this.selectedFilters);
      // Close the menu after saving
      this.closeMenu(menuIndex);
    },
    handleSaveFilter() {
      // Legacy method - kept for backward compatibility
      this.saveFilter(this.selectedFilters);
    },
    closeMenu(index) {
      this.$set(this.menuOpen, index, false);
      this.filterStage = 0;
    },
  
  },
};
</script>
